import React, { useState } from 'react';
import { Table, Button } from 'react-bootstrap';
import { useLanguage } from '../context/LanguageContext';

const Calendar: React.FC = () => {
  // Updated team members to match TimeSheet
  const teamMembers = ['Jacky', 'Henry', 'Kenneth', 'Linda', 'Angela', 'Crystal', 'Nicole', 'Windy'];
  
  // Generate dates starting from a Monday (2025-12-29 is a Monday)
  const generateDates = (startDate: Date, count: number): string[] => {
    const dates: string[] = [];
    const currentDate = new Date(startDate);
    
    // Find the Monday of the current week
    const dayOfWeek = currentDate.getDay();
    const diffToMonday = currentDate.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1); // Adjust for Sunday
    const monday = new Date(currentDate.setDate(diffToMonday));
    
    const actualMonday = new Date(monday);
    for (let i = 0; i < count; i++) {
      const date = new Date(actualMonday);
      date.setDate(actualMonday.getDate() + i);
      dates.push(date.toISOString().split('T')[0]); // Format as YYYY-MM-DD
    }
    
    return dates;
  };

  // Initial dates (7 days starting from a Monday)
  const [dates, setDates] = useState<string[]>(() => generateDates(new Date('2025-12-29'), 7));
  const [currentStartIndex, setCurrentStartIndex] = useState(0);
  const [visibleDaysCount] = useState(7);

  // State for calendar data
  const [calendarData, setCalendarData] = useState<Record<string, string>>({});
  
  // State for selected member
  const [selectedMember, setSelectedMember] = useState<string | null>(null);
  
  // State for project data (for the bottom table)
  const [projectData, setProjectData] = useState<Record<string, string>>({});
  
  // Projects list
  const projects = ['Project A', 'Project B', 'Project C', 'Project D', 'Project E'];
  
  // State for editing
  const [editingCell, setEditingCell] = useState<{ type: 'calendar' | 'project'; key: string } | null>(null);
  const [editValue, setEditValue] = useState('');

  // Navigate to previous week
  const goToPreviousWeek = () => {
    const newStartIndex = Math.max(0, currentStartIndex - 1); // Move back one week
    const newStartDate = new Date('2025-12-29');
    newStartDate.setDate(newStartDate.getDate() + newStartIndex * 7);
    setCurrentStartIndex(newStartIndex);
    setDates(generateDates(newStartDate, 7));
  };

  // Navigate to next week
  const goToNextWeek = () => {
    const newStartIndex = currentStartIndex + 1; // Move forward one week
    const newStartDate = new Date('2025-12-29');
    newStartDate.setDate(newStartDate.getDate() + newStartIndex * 7);
    setCurrentStartIndex(newStartIndex);
    setDates(generateDates(newStartDate, 7));
  };

  // Check if a date is weekend (Saturday or Sunday)
  const isWeekend = (dateString: string): boolean => {
    const date = new Date(dateString);
    const dayOfWeek = date.getDay();
    return dayOfWeek === 0 || dayOfWeek === 6; // Sunday is 0, Saturday is 6
  };

  // Check if a date is New Year's Day (2026-01-01)
  const isNewYearsDay = (dateString: string): boolean => {
    return dateString === '2026-01-01';
  };

  // Handle cell click to start editing
  const handleCellClick = (type: 'calendar' | 'project', key: string) => {
    setEditingCell({ type, key });
    if (type === 'calendar') {
      setEditValue(calendarData[key] || '');
    } else {
      setEditValue(projectData[key] || '');
    }
  };

  // Save edited value
  const saveEdit = () => {
    if (editingCell) {
      if (editingCell.type === 'calendar') {
        setCalendarData(prev => ({
          ...prev,
          [editingCell.key]: editValue
        }));
      } else {
        setProjectData(prev => ({
          ...prev,
          [editingCell.key]: editValue
        }));
      }
      setEditingCell(null);
      setEditValue('');
    }
  };

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditValue(e.target.value);
  };

  // Handle key press in input
  const handleInputKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      saveEdit();
    } else if (e.key === 'Escape') {
      setEditingCell(null);
      setEditValue('');
    }
  };

  // Handle member selection
  const handleMemberSelect = (member: string) => {
    setSelectedMember(member);
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    // Calculate day of week with Monday as 0: Monday=0, Tuesday=1, ..., Sunday=6
    const dayOfWeek = date.getDay();
    const adjustedDay = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Sunday becomes 6, others shift down by 1
    const dayNames = [t('monday'), t('tuesday'), t('wednesday'), t('thursday'), t('friday'), t('saturday'), t('sunday')];
    const dayName = dayNames[adjustedDay];
    return `${dateString} (${dayName})`;
  };

  const { t } = useLanguage();

  return (
    <div className="calendar">
      <h2>{t('calendar')}</h2>

      <div className="calendar-container">
        <div className="d-flex justify-content-between mb-3">
          <Button
            variant="primary"
            onClick={goToPreviousWeek}
            disabled={currentStartIndex === 0}
          >
            &lt; {t('previousWeek')}
          </Button>
          <div>
            {dates[0]} {t('to')} {dates[dates.length - 1]}
          </div>
          <Button variant="primary" onClick={goToNextWeek}>
            {t('nextWeek')} &gt;
          </Button>
        </div>

        <Table responsive className="calendar-table">
          <thead>
            <tr>
              <th>
                <div>{t('member')}</div>
                <div className="text-muted small">(成員)</div>
              </th>
              {dates.map(date => (
                <th
                  key={date}
                  className="text-center"
                  style={{ backgroundColor: isWeekend(date) || isNewYearsDay(date) ? '#ffcccc' : 'inherit' }}
                >
                  {formatDate(date)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {teamMembers.map(member => (
              <tr key={member} className={selectedMember === member ? 'table-primary' : ''}>
                <td className="member-cell">
                  <Button 
                    variant={selectedMember === member ? "primary" : "outline-primary"} 
                    size="sm"
                    onClick={() => handleMemberSelect(member)}
                  >
                    {member}
                  </Button>
                </td>
                {dates.map(date => {
                  const key = `${member}-${date}`;
                  const isEditing = editingCell?.type === 'calendar' && editingCell.key === key;
                  
                  return (
                    <td 
                      key={key}
                      style={{ 
                        backgroundColor: isWeekend(date) || isNewYearsDay(date) ? '#ffcccc' : 'inherit',
                        height: '100px',
                        overflow: 'auto',
                        border: '1px solid #dee2e6',
                        verticalAlign: 'top'
                      }}
                      onClick={() => handleCellClick('calendar', key)}
                    >
                      {isEditing ? (
                        <input
                          type="text"
                          value={editValue}
                          onChange={handleInputChange}
                          onBlur={saveEdit}
                          onKeyDown={handleInputKeyPress}
                          autoFocus
                          style={{ width: '100%' }}
                        />
                      ) : (
                        <div>{calendarData[key] || ''}</div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {selectedMember && (
        <div className="project-calendar mt-4">
          <h3>{t('projectsFor')} {selectedMember}</h3>
          <div className="d-flex justify-content-between mb-3">
            <Button
              variant="secondary"
              onClick={goToPreviousWeek}
              disabled={currentStartIndex === 0}
            >
              &lt; {t('previousWeek')}
            </Button>
            <div>
              {dates[0]} {t('to')} {dates[dates.length - 1]}
            </div>
            <Button variant="secondary" onClick={goToNextWeek}>
              {t('nextWeek')} &gt;
            </Button>
          </div>

          <Table responsive className="calendar-table">
            <thead>
              <tr>
                <th>
                  <div>{t('project')}</div>
                  <div className="text-muted small">(項目)</div>
                </th>
                {dates.map(date => (
                  <th
                    key={date}
                    className="text-center"
                    style={{ backgroundColor: isWeekend(date) || isNewYearsDay(date) ? '#ffcccc' : 'inherit' }}
                  >
                    {formatDate(date)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {projects.map(project => (
                <tr key={project}>
                  <td className="project-cell">{project}</td>
                  {dates.map(date => {
                    const key = `${project}-${date}`;
                    const isEditing = editingCell?.type === 'project' && editingCell.key === key;
                    
                    return (
                      <td 
                        key={key}
                        style={{ 
                          backgroundColor: isWeekend(date) || isNewYearsDay(date) ? '#ffcccc' : 'inherit',
                          height: '100px',
                          overflow: 'auto',
                          border: '1px solid #dee2e6',
                          verticalAlign: 'top'
                        }}
                        onClick={() => handleCellClick('project', key)}
                      >
                        {isEditing ? (
                          <input
                            type="text"
                            value={editValue}
                            onChange={handleInputChange}
                            onBlur={saveEdit}
                            onKeyDown={handleInputKeyPress}
                            autoFocus
                            style={{ width: '100%' }}
                          />
                        ) : (
                          <div>{projectData[key] || ''}</div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default Calendar;